'use client';

function _typeof(o) { "@babel/helpers - typeof"; return _typeof = "function" == typeof Symbol && "symbol" == typeof Symbol.iterator ? function (o) { return typeof o; } : function (o) { return o && "function" == typeof Symbol && o.constructor === Symbol && o !== Symbol.prototype ? "symbol" : typeof o; }, _typeof(o); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == _typeof(i) ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != _typeof(t) || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != _typeof(i)) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
var __rest = this && this.__rest || function (s, e) {
  var t = {};
  for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0) t[p] = s[p];
  if (s != null && typeof Object.getOwnPropertySymbols === "function") for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
    if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i])) t[p[i]] = s[p[i]];
  }
  return t;
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef } from 'react';
import makeCancellable from 'make-cancellable-promise';
import makeEventProps from 'make-event-props';
import clsx from 'clsx';
import mergeRefs from 'merge-refs';
import invariant from 'tiny-invariant';
import warning from 'warning';
import PageContext from './PageContext.js';
import Message from './Message.js';
import Canvas from './Page/Canvas.js';
import TextLayer from './Page/TextLayer.js';
import AnnotationLayer from './Page/AnnotationLayer.js';
import { cancelRunningTask, isProvided, makePageCallback } from './shared/utils.js';
import useDocumentContext from './shared/hooks/useDocumentContext.js';
import useResolver from './shared/hooks/useResolver.js';
var defaultScale = 1;
/**
 * Displays a page.
 *
 * Should be placed inside `<Document />`. Alternatively, it can have `pdf` prop passed, which can be obtained from `<Document />`'s `onLoadSuccess` callback function, however some advanced functions like linking between pages inside a document may not be working correctly.
 */
export default function Page(props) {
  var documentContext = useDocumentContext();
  var mergedProps = Object.assign(Object.assign({}, documentContext), props);
  var _mergedProps$_classNa = mergedProps._className,
    _className = _mergedProps$_classNa === void 0 ? 'react-pdf__Page' : _mergedProps$_classNa,
    _mergedProps$_enableR = mergedProps._enableRegisterUnregisterPage,
    _enableRegisterUnregisterPage = _mergedProps$_enableR === void 0 ? true : _mergedProps$_enableR,
    canvasBackground = mergedProps.canvasBackground,
    canvasRef = mergedProps.canvasRef,
    children = mergedProps.children,
    className = mergedProps.className,
    CustomRenderer = mergedProps.customRenderer,
    customTextRenderer = mergedProps.customTextRenderer,
    devicePixelRatio = mergedProps.devicePixelRatio,
    _mergedProps$error = mergedProps.error,
    error = _mergedProps$error === void 0 ? 'Failed to load the page.' : _mergedProps$error,
    height = mergedProps.height,
    inputRef = mergedProps.inputRef,
    _mergedProps$loading = mergedProps.loading,
    loading = _mergedProps$loading === void 0 ? 'Loading page…' : _mergedProps$loading,
    _mergedProps$noData = mergedProps.noData,
    noData = _mergedProps$noData === void 0 ? 'No page specified.' : _mergedProps$noData,
    onGetAnnotationsErrorProps = mergedProps.onGetAnnotationsError,
    onGetAnnotationsSuccessProps = mergedProps.onGetAnnotationsSuccess,
    onGetStructTreeErrorProps = mergedProps.onGetStructTreeError,
    onGetStructTreeSuccessProps = mergedProps.onGetStructTreeSuccess,
    onGetTextErrorProps = mergedProps.onGetTextError,
    onGetTextSuccessProps = mergedProps.onGetTextSuccess,
    onLoadErrorProps = mergedProps.onLoadError,
    onLoadSuccessProps = mergedProps.onLoadSuccess,
    onRenderAnnotationLayerErrorProps = mergedProps.onRenderAnnotationLayerError,
    onRenderAnnotationLayerSuccessProps = mergedProps.onRenderAnnotationLayerSuccess,
    onRenderErrorProps = mergedProps.onRenderError,
    onRenderSuccessProps = mergedProps.onRenderSuccess,
    onRenderTextLayerErrorProps = mergedProps.onRenderTextLayerError,
    onRenderTextLayerSuccessProps = mergedProps.onRenderTextLayerSuccess,
    pageIndexProps = mergedProps.pageIndex,
    pageNumberProps = mergedProps.pageNumber,
    pdf = mergedProps.pdf,
    registerPage = mergedProps.registerPage,
    _mergedProps$renderAn = mergedProps.renderAnnotationLayer,
    renderAnnotationLayerProps = _mergedProps$renderAn === void 0 ? true : _mergedProps$renderAn,
    _mergedProps$renderFo = mergedProps.renderForms,
    renderForms = _mergedProps$renderFo === void 0 ? false : _mergedProps$renderFo,
    _mergedProps$renderMo = mergedProps.renderMode,
    renderMode = _mergedProps$renderMo === void 0 ? 'canvas' : _mergedProps$renderMo,
    _mergedProps$renderTe = mergedProps.renderTextLayer,
    renderTextLayerProps = _mergedProps$renderTe === void 0 ? true : _mergedProps$renderTe,
    rotateProps = mergedProps.rotate,
    _mergedProps$scale = mergedProps.scale,
    scaleProps = _mergedProps$scale === void 0 ? defaultScale : _mergedProps$scale,
    unregisterPage = mergedProps.unregisterPage,
    width = mergedProps.width,
    otherProps = __rest(mergedProps, ["_className", "_enableRegisterUnregisterPage", "canvasBackground", "canvasRef", "children", "className", "customRenderer", "customTextRenderer", "devicePixelRatio", "error", "height", "inputRef", "loading", "noData", "onGetAnnotationsError", "onGetAnnotationsSuccess", "onGetStructTreeError", "onGetStructTreeSuccess", "onGetTextError", "onGetTextSuccess", "onLoadError", "onLoadSuccess", "onRenderAnnotationLayerError", "onRenderAnnotationLayerSuccess", "onRenderError", "onRenderSuccess", "onRenderTextLayerError", "onRenderTextLayerSuccess", "pageIndex", "pageNumber", "pdf", "registerPage", "renderAnnotationLayer", "renderForms", "renderMode", "renderTextLayer", "rotate", "scale", "unregisterPage", "width"]);
  var _useResolver = useResolver(),
    _useResolver2 = _slicedToArray(_useResolver, 2),
    pageState = _useResolver2[0],
    pageDispatch = _useResolver2[1];
  var page = pageState.value,
    pageError = pageState.error;
  var pageElement = useRef(null);
  invariant(pdf, 'Attempted to load a page, but no document was specified. Wrap <Page /> in a <Document /> or pass explicit `pdf` prop.');
  var pageIndex = isProvided(pageNumberProps) ? pageNumberProps - 1 : pageIndexProps !== null && pageIndexProps !== void 0 ? pageIndexProps : null;
  var pageNumber = pageNumberProps !== null && pageNumberProps !== void 0 ? pageNumberProps : isProvided(pageIndexProps) ? pageIndexProps + 1 : null;
  var rotate = rotateProps !== null && rotateProps !== void 0 ? rotateProps : page ? page.rotate : null;
  var scale = useMemo(function () {
    if (!page) {
      return null;
    }
    // Be default, we'll render page at 100% * scale width.
    var pageScale = 1;
    // Passing scale explicitly null would cause the page not to render
    var scaleWithDefault = scaleProps !== null && scaleProps !== void 0 ? scaleProps : defaultScale;
    // If width/height is defined, calculate the scale of the page so it could be of desired width.
    if (width || height) {
      var viewport = page.getViewport({
        scale: 1,
        rotation: rotate
      });
      if (width) {
        pageScale = width / viewport.width;
      } else if (height) {
        pageScale = height / viewport.height;
      }
    }
    return scaleWithDefault * pageScale;
  }, [height, page, rotate, scaleProps, width]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect intentionally triggered on pdf change
  useEffect(function hook() {
    return function () {
      if (!isProvided(pageIndex)) {
        // Impossible, but TypeScript doesn't know that
        return;
      }
      if (_enableRegisterUnregisterPage && unregisterPage) {
        unregisterPage(pageIndex);
      }
    };
  }, [_enableRegisterUnregisterPage, pdf, pageIndex, unregisterPage]);
  /**
   * Called when a page is loaded successfully
   */
  function onLoadSuccess() {
    if (onLoadSuccessProps) {
      if (!page || !scale) {
        // Impossible, but TypeScript doesn't know that
        return;
      }
      onLoadSuccessProps(makePageCallback(page, scale));
    }
    if (_enableRegisterUnregisterPage && registerPage) {
      if (!isProvided(pageIndex) || !pageElement.current) {
        // Impossible, but TypeScript doesn't know that
        return;
      }
      registerPage(pageIndex, pageElement.current);
    }
  }
  /**
   * Called when a page failed to load
   */
  function onLoadError() {
    if (!pageError) {
      // Impossible, but TypeScript doesn't know that
      return;
    }
    warning(false, pageError.toString());
    if (onLoadErrorProps) {
      onLoadErrorProps(pageError);
    }
  }
  // biome-ignore lint/correctness/useExhaustiveDependencies: useEffect intentionally triggered on pdf and pageIndex change
  useEffect(function resetPage() {
    pageDispatch({
      type: 'RESET'
    });
  }, [pageDispatch, pdf, pageIndex]);
  useEffect(function loadPage() {
    if (!pdf || !pageNumber) {
      return;
    }
    var cancellable = makeCancellable(pdf.getPage(pageNumber));
    var runningTask = cancellable;
    cancellable.promise.then(function (nextPage) {
      pageDispatch({
        type: 'RESOLVE',
        value: nextPage
      });
    }).catch(function (error) {
      pageDispatch({
        type: 'REJECT',
        error: error
      });
    });
    return function () {
      return cancelRunningTask(runningTask);
    };
  }, [pageDispatch, pdf, pageNumber]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: Ommitted callbacks so they are not called every time they change
  useEffect(function () {
    if (page === undefined) {
      return;
    }
    if (page === false) {
      onLoadError();
      return;
    }
    onLoadSuccess();
  }, [page, scale]);
  var childContext = useMemo(function () {
    return (
      // Technically there cannot be page without pageIndex, pageNumber, rotate and scale, but TypeScript doesn't know that
      page && isProvided(pageIndex) && pageNumber && isProvided(rotate) && isProvided(scale) ? {
        _className: _className,
        canvasBackground: canvasBackground,
        customTextRenderer: customTextRenderer,
        devicePixelRatio: devicePixelRatio,
        onGetAnnotationsError: onGetAnnotationsErrorProps,
        onGetAnnotationsSuccess: onGetAnnotationsSuccessProps,
        onGetStructTreeError: onGetStructTreeErrorProps,
        onGetStructTreeSuccess: onGetStructTreeSuccessProps,
        onGetTextError: onGetTextErrorProps,
        onGetTextSuccess: onGetTextSuccessProps,
        onRenderAnnotationLayerError: onRenderAnnotationLayerErrorProps,
        onRenderAnnotationLayerSuccess: onRenderAnnotationLayerSuccessProps,
        onRenderError: onRenderErrorProps,
        onRenderSuccess: onRenderSuccessProps,
        onRenderTextLayerError: onRenderTextLayerErrorProps,
        onRenderTextLayerSuccess: onRenderTextLayerSuccessProps,
        page: page,
        pageIndex: pageIndex,
        pageNumber: pageNumber,
        renderForms: renderForms,
        renderTextLayer: renderTextLayerProps,
        rotate: rotate,
        scale: scale
      } : null
    );
  }, [_className, canvasBackground, customTextRenderer, devicePixelRatio, onGetAnnotationsErrorProps, onGetAnnotationsSuccessProps, onGetStructTreeErrorProps, onGetStructTreeSuccessProps, onGetTextErrorProps, onGetTextSuccessProps, onRenderAnnotationLayerErrorProps, onRenderAnnotationLayerSuccessProps, onRenderErrorProps, onRenderSuccessProps, onRenderTextLayerErrorProps, onRenderTextLayerSuccessProps, page, pageIndex, pageNumber, renderForms, renderTextLayerProps, rotate, scale]);
  var eventProps = useMemo(function () {
    return makeEventProps(otherProps, function () {
      return page ? scale ? makePageCallback(page, scale) : undefined : page;
    });
  },
  // biome-ignore lint/correctness/useExhaustiveDependencies: FIXME
  [otherProps, page, scale]);
  var pageKey = "".concat(pageIndex, "@").concat(scale, "/").concat(rotate);
  function renderMainLayer() {
    switch (renderMode) {
      case 'custom':
        {
          invariant(CustomRenderer, "renderMode was set to \"custom\", but no customRenderer was passed.");
          return _jsx(CustomRenderer, {}, "".concat(pageKey, "_custom"));
        }
      case 'none':
        return null;
      case 'canvas':
      default:
        return _jsx(Canvas, {
          canvasRef: canvasRef
        }, "".concat(pageKey, "_canvas"));
    }
  }
  function renderTextLayer() {
    if (!renderTextLayerProps) {
      return null;
    }
    return _jsx(TextLayer, {}, "".concat(pageKey, "_text"));
  }
  function renderAnnotationLayer() {
    if (!renderAnnotationLayerProps) {
      return null;
    }
    return _jsx(AnnotationLayer, {}, "".concat(pageKey, "_annotations"));
  }
  function renderChildren() {
    return _jsxs(PageContext.Provider, {
      value: childContext,
      children: [renderMainLayer(), renderTextLayer(), renderAnnotationLayer(), children]
    });
  }
  function renderContent() {
    if (!pageNumber) {
      return _jsx(Message, {
        type: "no-data",
        children: typeof noData === 'function' ? noData() : noData
      });
    }
    if (pdf === null || page === undefined || page === null) {
      return _jsx(Message, {
        type: "loading",
        children: typeof loading === 'function' ? loading() : loading
      });
    }
    if (pdf === false || page === false) {
      return _jsx(Message, {
        type: "error",
        children: typeof error === 'function' ? error() : error
      });
    }
    return renderChildren();
  }
  return _jsx("div", Object.assign({
    className: clsx(_className, className),
    "data-page-number": pageNumber,
    // Assertion is needed for React 18 compatibility
    ref: mergeRefs(inputRef, pageElement),
    style: _defineProperty(_defineProperty(_defineProperty(_defineProperty(_defineProperty({}, '--scale-factor', "".concat(scale)), "backgroundColor", canvasBackground || 'white'), "position", 'relative'), "minWidth", 'min-content'), "minHeight", 'min-content')
  }, eventProps, {
    children: renderContent()
  }));
}